import { useState } from 'react';
import {
  Accordion, AccordionSummary, AccordionDetails, Box, Button, Chip, Checkbox, FormControlLabel, FormControl, FormLabel,
  Grid, IconButton, LinearProgress, Link, MenuItem, Radio, RadioGroup, Stack, TextField, Typography, Alert,
} from '@mui/material';
import { ExpandMore, Delete, CloudUpload, CheckCircle, RadioButtonUnchecked } from '@mui/icons-material';
import { text } from '../../theme/colors';

const STATUS_CHIP = {
  missing: { label: 'To do', color: 'default' }, pending: { label: 'Filled', color: 'info' }, flagged: { label: 'Needs more info', color: 'warning' },
  approved: { label: 'Verified', color: 'success' }, rejected: { label: 'Rejected', color: 'error' },
};

function FieldInput({ field, value, onChange, disabled }) {
  // A handful of choices reads better laid out than hidden in a menu (owner, 2026-09-28).
  if (field.type === 'select' && (field.options || []).length <= 4) {
    return (
      <FormControl disabled={disabled} required={field.required}>
        <FormLabel sx={{ fontSize: 14 }}>{field.label}</FormLabel>
        <RadioGroup row value={value ?? ''} onChange={(e) => onChange(e.target.value)}>
          {field.options.map((o) => <FormControlLabel key={o} value={o} control={<Radio size="small" />} label={field.optionLabels?.[o] || o} />)}
        </RadioGroup>
      </FormControl>
    );
  }
  if (field.type === 'select') {
    return (
      <TextField select fullWidth size="small" label={field.label} required={field.required} disabled={disabled}
        value={value ?? ''} onChange={(e) => onChange(e.target.value)}>
        {(field.options || []).map((o) => <MenuItem key={o} value={o}>{field.optionLabels?.[o] || o}</MenuItem>)}
      </TextField>
    );
  }
  if (field.type === 'bool') {
    // A required yes/no must be answered deliberately — a checkbox cannot tell "No" from "not yet answered".
    if (field.required) {
      return (
        <FormControl disabled={disabled} required>
          <FormLabel sx={{ fontSize: 14 }}>{field.label}</FormLabel>
          <RadioGroup row value={value === true ? 'yes' : value === false ? 'no' : ''} onChange={(e) => onChange(e.target.value === 'yes')}>
            <FormControlLabel value="yes" control={<Radio size="small" />} label="Yes" />
            <FormControlLabel value="no" control={<Radio size="small" />} label="No" />
          </RadioGroup>
        </FormControl>
      );
    }
    return <FormControlLabel control={<Checkbox checked={value === true} indeterminate={value === undefined} disabled={disabled} onChange={(e) => onChange(e.target.checked)} />} label={field.label} />;
  }
  return (
    <TextField fullWidth size="small" label={field.label} required={field.required} disabled={disabled}
      type={field.type === 'number' ? 'number' : field.type === 'date' ? 'date' : 'text'}
      InputLabelProps={field.type === 'date' ? { shrink: true } : undefined}
      multiline={field.key === 'comparables' || field.key.endsWith('_note') || field.key === 'servitudes'}
      value={value ?? ''} onChange={(e) => onChange(field.type === 'number' ? (e.target.value === '' ? '' : Number(e.target.value)) : e.target.value)} />
  );
}

// In review mode the admin reads the realtor's answers — no inputs, so nothing can be typed over.
function FieldReadOnly({ field, value }) {
  const shown = field.type === 'bool' ? (value === true ? 'Yes' : value === false ? 'No' : '—')
    : (value === undefined || value === null || value === '' ? '—' : String(field.optionLabels?.[value] || value));
  return (
    <Box>
      <Typography variant="caption" sx={{ color: text.secondary }}>{field.label}</Typography>
      <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>{shown}</Typography>
    </Box>
  );
}

function Item({ item, state, req, docs, editable, canDelete = editable, reviewMode, canDecide, expanded, onSaveRequirement, onUploadDocument, onDeleteDocument, onReview }) {
  const [fields, setFields] = useState(req?.fields || {});
  const [na, setNa] = useState(req?.status === 'not_applicable');
  const [naReason, setNaReason] = useState(req?.na_reason || '');
  const [uploading, setUploading] = useState(null); // document type key being uploaded
  const [progress, setProgress] = useState(0);
  const [busy, setBusy] = useState(false);
  const [notes, setNotes] = useState('');
  // An N/A item counts as approved for the dossier maths, but saying "Verified" before an
  // admin has looked at it overstates it — show the claim until someone accepts it.
  const chip = state.rawStatus === 'not_applicable'
    ? (state.reviewedBy ? { label: 'N/A accepted', color: 'success' } : { label: 'Not applicable', color: 'default' })
    : (STATUS_CHIP[state.status] || STATUS_CHIP.missing);
  const labelsFor = (keys) => (keys || []).map((k) => (item.fields.find((f) => f.key === k) || {}).label || k);
  const expired = labelsFor(state.expiredFields);
  const invalid = labelsFor((state.invalidFields || []).filter((k) => !(state.expiredFields || []).includes(k)));
  const naAllowed = item.allowNotApplicable !== false;
  const missing = labelsFor(state.missingFields);

  const save = async () => {
    setBusy(true);
    try { await onSaveRequirement(item.key, na ? { notApplicable: true, naReason } : { fields }); } finally { setBusy(false); }
  };
  const review = async (decision) => {
    setBusy(true);
    try { await onReview(item.key, decision, notes); setNotes(''); } finally { setBusy(false); }
  };
  // One slot per kind of document, each taking one or more files.
  const upload = (docType) => async (e) => {
    const files = Array.from(e.target.files || []); if (!files.length) return;
    setBusy(true); setUploading(docType);
    try { for (const file of files) { setProgress(0); await onUploadDocument(item.key, docType, file, setProgress); } }
    finally { setBusy(false); setUploading(null); setProgress(0); e.target.value = ''; }
  };
  const typeKeys = item.documentTypes.map((t) => t.key);
  const slots = [
    ...item.documentTypes.map((t) => ({ ...t, files: docs.filter((d) => d.document_type === t.key) })),
    // Files under a type the template no longer lists still need to be seen.
    ...(docs.some((d) => !typeKeys.includes(d.document_type)) ? [{ key: '_other', label: 'Other files', legacy: true, files: docs.filter((d) => !typeKeys.includes(d.document_type)) }] : []),
  ];

  return (
    <Accordion disableGutters defaultExpanded={!!expanded}>
      <AccordionSummary expandIcon={<ExpandMore />}>
        <Stack direction="row" spacing={1.5} alignItems="center" sx={{ flex: 1, pr: 1 }}>
          <Typography sx={{ fontWeight: 600, flex: 1 }}>{item.label}</Typography>
          <Typography variant="caption" sx={{ color: text.secondary, display: { xs: 'none', sm: 'block' } }}>{item.authority}</Typography>
          {state.docCount > 0 && <Chip size="small" variant="outlined" label={`${state.docCount} file${state.docCount === 1 ? '' : 's'}`} />}
          <Chip size="small" color={chip.color} label={chip.label} />
        </Stack>
      </AccordionSummary>
      <AccordionDetails>
        {state.reviewerNotes && !reviewMode && <Alert severity={state.status === 'rejected' ? 'error' : 'warning'} sx={{ mb: 2 }}>From Eqaya: {state.reviewerNotes}</Alert>}
        {expired.length > 0 && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {reviewMode ? 'This mandate has expired. It cannot be verified until the realtor uploads the renewed one.' : 'This mandate has expired. Ask the owner to sign a new one, upload it and enter the new expiry date.'}
          </Alert>
        )}
        {invalid.length > 0 && !reviewMode && <Alert severity="warning" sx={{ mb: 2 }}>Re-enter: {invalid.join(', ')}</Alert>}
        {/* An item marked N/A has no fields to fill, so the list of missing ones is noise; an
            untouched item should not open with a warning about what it is missing either. */}
        {missing.length > 0 && !reviewMode && !na && state.rawStatus !== 'missing'
          && <Alert severity="info" sx={{ mb: 2 }}>Still needed: {missing.join(', ')}</Alert>}
        {!reviewMode && naAllowed && (
          <FormControlLabel sx={{ mb: 1 }} control={<Checkbox checked={na} disabled={!editable} onChange={(e) => setNa(e.target.checked)} />} label="This item does not apply to this property" />
        )}
        {na && !reviewMode ? (
          <TextField fullWidth size="small" label="Why it does not apply" value={naReason} disabled={!editable} onChange={(e) => setNaReason(e.target.value)} sx={{ mb: 2 }} />
        ) : (
          <Grid container spacing={2} sx={{ mb: 2 }}>
            {item.fields.map((f) => (
              <Grid item xs={12} sm={f.type === 'bool' ? 12 : 6} key={f.key}>
                {reviewMode
                  ? <FieldReadOnly field={f} value={fields[f.key]} />
                  : <FieldInput field={f} value={fields[f.key]} disabled={!editable} onChange={(v) => setFields({ ...fields, [f.key]: v })} />}
              </Grid>
            ))}
          </Grid>
        )}
        {req?.na_reason && reviewMode && <Alert severity="info" sx={{ mb: 2 }}>Marked not applicable: {req.na_reason}</Alert>}

        <Typography variant="subtitle2" sx={{ mb: 1 }}>Documents {item.allowNoDocument ? '(optional for this item)' : item.documentTypes.length > 1 ? '(at least one required; upload each one you have)' : '(required)'}</Typography>
        <Stack spacing={1} sx={{ mb: 1 }}>
          {slots.map((slot) => (
            <Box key={slot.key} sx={{ p: 1.5, borderRadius: '10px', border: '1px solid', borderColor: slot.files.length ? '#9fcfc6' : '#cfd8d6', bgcolor: slot.files.length ? '#f3faf8' : '#fff' }}>
              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} alignItems={{ sm: 'center' }}>
                <Stack direction="row" spacing={1} alignItems="center" sx={{ flex: 1, minWidth: 0 }}>
                  {slot.files.length ? <CheckCircle sx={{ color: '#134e4a', fontSize: 20, flexShrink: 0 }} /> : <RadioButtonUnchecked sx={{ color: '#94a3b8', fontSize: 20, flexShrink: 0 }} />}
                  <Typography sx={{ fontWeight: 600, fontSize: 14.5 }}>{slot.label}</Typography>
                </Stack>
                {editable && !reviewMode && !slot.legacy && (
                  <Button size="small" variant="outlined" component="label" startIcon={<CloudUpload />} disabled={busy} sx={{ flexShrink: 0, textTransform: 'none', fontWeight: 700 }}>
                    {slot.files.length ? 'Add another' : 'Upload'}
                    <input hidden multiple type="file" accept=".pdf,.jpg,.jpeg,.png,.doc,.docx" onChange={upload(slot.key)} aria-label={`Upload ${slot.label}`} />
                  </Button>
                )}
              </Stack>
              {slot.files.map((d) => (
                <Stack key={d.id} direction="row" spacing={1} alignItems="center" sx={{ mt: 0.75, pl: 3.5 }}>
                  <Link href={d.file_url} target="_blank" rel="noopener noreferrer" sx={{ flex: 1, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', fontSize: 14 }}>{d.original_name || 'document'}</Link>
                  {canDelete && !reviewMode && <IconButton size="small" aria-label={`Delete ${d.original_name || 'document'}`} onClick={() => onDeleteDocument(d.id)}><Delete fontSize="small" /></IconButton>}
                </Stack>
              ))}
              {uploading === slot.key && progress > 0 && <LinearProgress variant="determinate" value={progress} sx={{ mt: 1 }} />}
            </Box>
          ))}
        </Stack>

        <Box sx={{ mt: 2, display: 'flex', gap: 1, flexWrap: 'wrap', alignItems: 'center' }}>
          {editable && !reviewMode && <Button variant="contained" onClick={save} disabled={busy || (na && !naReason.trim())}>Save item</Button>}
          {reviewMode && canDecide && (
            <>
              <TextField size="small" label="Note to realtor" value={notes} onChange={(e) => setNotes(e.target.value)} sx={{ flex: 1, minWidth: 240 }} />
              <Button variant="contained" color="success" disabled={busy} onClick={() => review('verified')}>Verify</Button>
              <Button variant="outlined" color="warning" disabled={busy || !notes.trim()} onClick={() => review('needs_more_info')}>Needs more info</Button>
              <Button variant="outlined" color="error" disabled={busy || !notes.trim()} onClick={() => review('rejected')}>Reject</Button>
            </>
          )}
        </Box>
      </AccordionDetails>
    </Accordion>
  );
}

/**
 * `editableKeys` / `decidableKeys` open single items outside the normal window:
 * the owner's mandate to sell can be renewed (and re-checked) on a listing that
 * is already verified or live. Files on a live listing can be added, not deleted.
 */
export default function DossierAccordion({ listing, keys = null, expanded = false, editable = false, editableKeys = [], reviewMode = false, canDecide = true, decidableKeys = [], onSaveRequirement, onUploadDocument, onDeleteDocument, onReview }) {
  if (!listing?.template) return null;
  // `keys` shows just those items (one step of the listing flow), opened when `expanded`.
  const template = keys ? listing.template.filter((i) => keys.includes(i.key)) : listing.template;
  return (
    <Box>
      {template.map((item) => {
        // Key on the requirement's own timestamp: a save re-syncs that item, and unrelated
        // listing saves (details, photos) leave unsaved edits in other items alone.
        const req = listing.requirements.find((r) => r.requirement_key === item.key);
        return (
        <Item key={`${item.key}-${req?.updated_at || ''}`} item={item}
          state={listing.dossier.items.find((i) => i.key === item.key) || { status: 'missing', docCount: 0 }}
          req={req}
          docs={listing.documents.filter((d) => d.requirement_key === item.key)}
          editable={editable || editableKeys.includes(item.key)} canDelete={editable} reviewMode={reviewMode} canDecide={canDecide || decidableKeys.includes(item.key)} expanded={expanded}
          onSaveRequirement={onSaveRequirement} onUploadDocument={onUploadDocument} onDeleteDocument={onDeleteDocument} onReview={onReview} />
        );
      })}
    </Box>
  );
}
